import { SxProps } from '@mui/material';
import dayjs from 'dayjs';
import { Duration } from 'dayjs/plugin/duration';
import { HTMLMotionProps, motion } from 'motion/react';
import { FunctionComponent, PropsWithChildren, ReactNode, useEffect } from 'react';
import { adaptTransition, AsyncAnimationProps, useAnimationsDisabled } from '../../util/Animation';
import { GameBoardZIndex } from '../game/GameBoardZIndex';
import { FullScreen, Widescreen } from '../layout/ScreenLayout';

type Props = PropsWithChildren<AsyncAnimationProps & {
	/** Animation style: 'wipe' slides in from left, 'fade' fades in */
	animationType: 'wipe' | 'fade';
	/** The message content to display */
	children: ReactNode;
	duration?: Duration;
	delay?: Duration;
	sx?: Omit<SxProps, 'width' | 'height'>;
}>;

/**
 * Full screen message overlay with animation
 * Uses the Widescreen component to ensure the contents scale correctly for the screen
 */
export const FullScreenMessage: FunctionComponent<Props> = (props) => {
	const { duration = dayjs.duration(1, 'seconds'), delay, animationType = 'wipe', } = props;
	const disabled = useAnimationsDisabled();

	const Anim = (animationType === 'wipe') ? Wipe : Fade;

	/** If animations are disabled we need to call props.onComplete */
	useEffect(function setupCompletion() {
		if (disabled && props.show && props.onComplete) {
			const id = setTimeout(() => props.onComplete?.(), duration.asMilliseconds());
			return () => clearTimeout(id);
		}
	}, [props.show, props.onComplete]);

	return props.show && <Anim
		transition={adaptTransition({
			duration: duration.asSeconds(),
			delay: delay?.asSeconds(),
		})}
		onAnimationComplete={props.onComplete}
		style={{
			position: 'fixed',
			top: 0,
			left: 0,
			width: '100vw',
			height: '100vh',
			zIndex: GameBoardZIndex.Transition,
		}}
	>
		<FullScreen sx={props.sx}>
			<Widescreen>
				{props.children}
			</Widescreen>
		</FullScreen>
	</Anim>;
};

const Wipe: FunctionComponent<HTMLMotionProps<'div'>> = (props) => {
	const animationsDisabled = useAnimationsDisabled();

	return <motion.div
		animate={{ x: 0 }}
		initial={animationsDisabled ? false : { x: '-100%' }}
		exit={animationsDisabled ? undefined : { x: '100%' }}
		{...props}
		transition={{ ease: 'easeInOut', ...props.transition }}
	/>;
};

const Fade: FunctionComponent<HTMLMotionProps<'div'>> = (props) => {
	const animationsDisabled = useAnimationsDisabled();

	return <motion.div
		initial={animationsDisabled ? false : { opacity: 0 }}
		animate={{ opacity: 1 }}
		exit={animationsDisabled ? undefined : { opacity: 0 }}
		{...props}
	/>;
};
